import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from "react";
import { getFriendlyErrorMessage } from "@/utils/errorMapper";
import type { BulkContact } from "../models/bulk-contact.model";

export type { BulkContact } from "../models/bulk-contact.model";

export interface CampaignConfig {
  messageTemplate: string;
  attachedFile: File | null;
  filePreview: string;
  isImage: boolean;
  delay: number;
  randomDelay: boolean;
}

interface CampaignHandlers {
  sendMessage: (phone: string, text: string, isGroup?: boolean, contact?: BulkContact) => Promise<boolean>;
  sendImage: (phone: string, base64: string, filename: string, caption: string, isGroup?: boolean, contact?: BulkContact) => Promise<boolean>;
  sendFile: (phone: string, base64: string, filename: string, caption: string, isGroup?: boolean, contact?: BulkContact) => Promise<boolean>;
  resolveLidToPhone: (lidJid: string) => Promise<string | null>;
}

type DesktopNotificationOptions = {
  body?: string;
  icon?: string;
  tag?: string;
};

interface BulkCampaignContextType {
  // States
  contacts: BulkContact[];
  isSending: boolean;
  isScheduled: boolean;
  sendProgress: { current: number; total: number };
  error: string | null;
  scheduleDate: string;
  scheduleTime: string;
  campaignConfig: CampaignConfig | null;
  showSummary: boolean;
  summaryStats: { sent: number; failed: number; total: number } | null;

  // Actions
  setContacts: React.Dispatch<React.SetStateAction<BulkContact[]>>;
  setShowSummary: (show: boolean) => void;
  registerHandlers: (handlers: CampaignHandlers) => void;
  startCampaign: (config: CampaignConfig) => Promise<void>;
  scheduleCampaign: (config: CampaignConfig, date: string, time: string) => void;
  cancelSchedule: () => void;
  stopCampaign: () => void;
  clearCampaign: () => void;
  retryFailedContacts: () => void;
  sendDesktopNotification: (title: string, options?: DesktopNotificationOptions) => void;
}

const BulkCampaignContext = createContext<BulkCampaignContextType | undefined>(undefined);

// Helper function to reconstruct a File object from serialized base64 preview
const fileFromBase64 = (base64: string, filename: string, mimeType: string): File => {
  const byteString = atob(base64);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  const blob = new Blob([ab], { type: mimeType });
  return new File([blob], filename, { type: mimeType });
};

export function BulkCampaignProvider({ children }: { children: React.ReactNode }) {
  const [contacts, setContacts] = useState<BulkContact[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [isScheduled, setIsScheduled] = useState(false);
  const [sendProgress, setSendProgress] = useState({ current: 0, total: 0 });
  const [error, setErrorState] = useState<string | null>(null);
  const setError = useCallback((err: string | null) => {
    setErrorState(err ? getFriendlyErrorMessage(err) : null);
  }, []);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  const [campaignConfig, setCampaignConfig] = useState<CampaignConfig | null>(null);

  // Summary Popup States
  const [showSummary, setShowSummary] = useState(false);
  const [summaryStats, setSummaryStats] = useState<{ sent: number; failed: number; total: number } | null>(null);

  const handlersRef = useRef<CampaignHandlers | null>(null);
  const abortRef = useRef(false);
  const scheduleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const contactsRef = useRef(contacts);
  const isSendingRef = useRef(isSending);
  const executeSendRef = useRef<((config: CampaignConfig) => Promise<void>) | undefined>(undefined);

  // Sync refs to avoid stale closures
  useEffect(() => {
    contactsRef.current = contacts;
  }, [contacts]);

  useEffect(() => {
    isSendingRef.current = isSending;
  }, [isSending]);

  // Request Notification Permissions on Mount
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.permission === "default" && Notification.requestPermission();
    }
  }, []);

  const sendDesktopNotification = useCallback((title: string, options?: DesktopNotificationOptions) => {
    if (!("Notification" in window)) return;
    if (Notification.permission === "granted") {
      new Notification(title, options);
    } else if (Notification.permission !== "denied") {
      Notification.requestPermission().then((permission) => {
        if (permission === "granted") {
          new Notification(title, options);
        }
      });
    }
  }, []);

  const registerHandlers = useCallback((handlers: CampaignHandlers) => {
    handlersRef.current = handlers;
  }, []);

  const replaceVariables = useCallback((template: string, contact: BulkContact): string => {
    let text = template;
    text = text.replace(/\{\{\s*name\s*\}\}/gi, contact.name);
    text = text.replace(/\{\{\s*phone\s*\}\}/gi, contact.phone);
    if (contact.csvData) {
      Object.entries(contact.csvData).forEach(([key, val]) => {
        const cleanKey = key.trim().toLowerCase();
        const regex = new RegExp(`\\{\\{\\s*${cleanKey}\\s*\\}\\}`, "gi");
        text = text.replace(regex, val || "");
      });
    }
    return text;
  }, []);

  const updateContactStatus = useCallback((id: string, status: "pending" | "sent" | "failed") => {
    setContacts((prev) =>
      prev.map((c) => (c.id === id ? { ...c, status } : c))
    );
  }, []);

  const executeSend = useCallback(async (config: CampaignConfig) => {
    if (isSendingRef.current) return;
    setIsSending(true);
    abortRef.current = false;
    setError(null);
    setShowSummary(false);

    const targets = contactsRef.current.filter((c) => c.selected && c.status === "pending");
    setSendProgress({ current: 0, total: targets.length });

    let succeeded = 0;
    let failed = 0;

    for (let i = 0; i < targets.length; i++) {
      if (abortRef.current) break;

      const contact = targets[i];
      let destPhone = contact.resolvedPhone || contact.phone;

      try {
        if (!handlersRef.current) {
          throw new Error("WhatsApp connection handlers are not initialized");
        }

        // Asynchronously resolve LID on the fly during sending loop (WhatsApp only)
        if (
          contact.platform !== "facebook" &&
          !contact.resolvedPhone &&
          contact.source === "chat" &&
          !contact.phone.includes("@") &&
          contact.phone.length > 10 &&
          handlersRef.current.resolveLidToPhone
        ) {
          try {
            const resolved = await handlersRef.current.resolveLidToPhone(`${contact.phone}@lid`);
            if (resolved) {
              destPhone = resolved;
              setContacts((prev) =>
                prev.map((c) => (c.id === contact.id ? { ...c, resolvedPhone: resolved } : c))
              );
            }
          } catch (e) {
            console.error("Failed to resolve LID phone in send loop:", e);
          }
        }

        const resolvedContact = { ...contact, resolvedPhone: destPhone !== contact.phone ? destPhone : contact.resolvedPhone };
        const personalizedText = replaceVariables(config.messageTemplate, resolvedContact);

        setSendProgress({ current: i + 1, total: targets.length });

        let success = false;
        if (config.attachedFile && config.filePreview) {
          const base64 = config.filePreview.split(",")[1];
          const destPhone = contact.resolvedPhone || contact.phone;
          if (config.isImage) {
            success = await handlersRef.current.sendImage(
              destPhone,
              base64,
              config.attachedFile.name,
              personalizedText,
              contact.isGroup,
              contact
            );
          } else {
            success = await handlersRef.current.sendFile(
              destPhone,
              base64,
              config.attachedFile.name,
              personalizedText,
              contact.isGroup,
              contact
            );
          }
        } else {
          success = await handlersRef.current.sendMessage(
            contact.resolvedPhone || contact.phone,
            personalizedText,
            contact.isGroup,
            contact
          );
        }

        updateContactStatus(contact.id, success ? "sent" : "failed");
        if (success) {
          succeeded++;
        } else {
          failed++;
          setError(`Failed to send message to ${contact.name}`);
        }
      } catch (err) {
        updateContactStatus(contact.id, "failed");
        failed++;
        setError(err instanceof Error ? err.message : "Failed to send message");
      }

      if (abortRef.current) break;

      if (i < targets.length - 1) {
        const actualDelay = config.randomDelay
          ? config.delay + Math.random() * config.delay
          : config.delay;
        await new Promise((r) => setTimeout(r, actualDelay * 1000));
      }
    }

    setIsSending(false);
    setIsScheduled(false);

    // Trigger Desktop Notification on Completion
    const totalSent = succeeded + failed;
    if (totalSent > 0) {
      let bodyText = `Successfully sent ${succeeded} messages.`;
      if (failed > 0) bodyText += ` ${failed} messages failed.`;
      sendDesktopNotification("Campaign Completed", {
        body: bodyText,
        icon: "/favicon.ico"
      });

      // Show summary dialog
      setSummaryStats({ sent: succeeded, failed, total: targets.length });
      setShowSummary(true);
    }
  }, [replaceVariables, updateContactStatus, sendDesktopNotification, setError]);

  useEffect(() => {
    executeSendRef.current = executeSend;
  }, [executeSend]);

  const startCampaign = useCallback(async (config: CampaignConfig) => {
    setCampaignConfig(config);
    await executeSend(config);
  }, [executeSend]);

  const scheduleCampaign = useCallback((config: CampaignConfig, date: string, time: string) => {
    if (!date || !time) return;

    if (scheduleTimerRef.current) {
      clearTimeout(scheduleTimerRef.current);
    }

    const [hours, minutes] = time.split(":").map(Number);
    const targetDate = new Date(date);
    targetDate.setHours(hours, minutes, 0, 0);

    const now = new Date();
    const diffMs = targetDate.getTime() - now.getTime();

    if (diffMs <= 0) {
      setError("Schedule time must be in the future");
      return;
    }

    setScheduleDate(date);
    setScheduleTime(time);
    setCampaignConfig(config);
    setIsScheduled(true);
    setError(null);

    scheduleTimerRef.current = setTimeout(() => {
      if (executeSendRef.current) {
        executeSendRef.current(config);
      }
    }, diffMs);
  }, [setError]);

  const cancelSchedule = useCallback(() => {
    if (scheduleTimerRef.current) {
      clearTimeout(scheduleTimerRef.current);
      scheduleTimerRef.current = null;
    }
    setIsScheduled(false);
    setScheduleDate("");
    setScheduleTime("");
    setCampaignConfig(null);
    localStorage.removeItem("wpp_bulk_campaign");
  }, []);

  const stopCampaign = useCallback(() => {
    abortRef.current = true;
    setIsSending(false);
  }, []);

  const clearCampaign = useCallback(() => {
    cancelSchedule();
    setContacts([]);
    setSendProgress({ current: 0, total: 0 });
    setError(null);
    setCampaignConfig(null);
    setIsSending(false);
    setShowSummary(false);
    setSummaryStats(null);
    localStorage.removeItem("wpp_bulk_campaign");
  }, [cancelSchedule, setError]);

  const retryFailedContacts = useCallback(() => {
    setContacts((prev) =>
      prev.map((c) => (c.status === "failed" && c.selected ? { ...c, status: "pending" } : c))
    );
    setShowSummary(false);

    if (campaignConfig) {
      setTimeout(() => {
        executeSend(campaignConfig);
      }, 300);
    }
  }, [campaignConfig, executeSend]);

  // Persist State to localStorage on changes (only if actively sending or scheduled)
  useEffect(() => {
    if (isSending || isScheduled) {
      const data = {
        contacts,
        isScheduled,
        scheduleDate,
        scheduleTime,
        wasActive: isSending, // Flag to indicate if the campaign was actively sending
        campaignConfig: campaignConfig ? {
          messageTemplate: campaignConfig.messageTemplate,
          isImage: campaignConfig.isImage,
          filePreview: campaignConfig.filePreview,
          attachedFile: campaignConfig.attachedFile ? {
            name: campaignConfig.attachedFile.name,
            size: campaignConfig.attachedFile.size,
            type: campaignConfig.attachedFile.type
          } : null,
          delay: campaignConfig.delay,
          randomDelay: campaignConfig.randomDelay
        } : null
      };
      localStorage.setItem("wpp_bulk_campaign", JSON.stringify(data));
    } else if (!isSending && !isScheduled && !showSummary) {
      // Clear storage if the campaign is completely finished/idle and summary is not active
      localStorage.removeItem("wpp_bulk_campaign");
    }
  }, [contacts, isSending, isScheduled, scheduleDate, scheduleTime, campaignConfig, showSummary]);

  // Restore State & Auto-Resume on Mount
  useEffect(() => {
    const raw = localStorage.getItem("wpp_bulk_campaign");
    if (!raw) return;
    try {
      const data = JSON.parse(raw);
      if (!data) return;

      let file: File | null = null;
      if (data.campaignConfig && data.campaignConfig.attachedFile && data.campaignConfig.filePreview) {
        try {
          const base64 = data.campaignConfig.filePreview.split(",")[1];
          file = fileFromBase64(base64, data.campaignConfig.attachedFile.name, data.campaignConfig.attachedFile.type);
        } catch (e) {
          console.error("Failed to reconstruct file from base64:", e);
        }
      }

      const config: CampaignConfig = data.campaignConfig ? {
        ...data.campaignConfig,
        attachedFile: file
      } : null;

      setContacts(data.contacts || []);
      setCampaignConfig(config);
      setIsScheduled(data.isScheduled || false);
      setScheduleDate(data.scheduleDate || "");
      setScheduleTime(data.scheduleTime || "");

      // ONLY resume if the campaign was actively sending when reloaded (wasActive === true)
      if (data.wasActive && data.contacts && data.contacts.some((c: any) => c.status === "pending" && c.selected)) {
        const checkHandlersInterval = setInterval(() => {
          if (handlersRef.current) {
            clearInterval(checkHandlersInterval);
            executeSend(config);
          }
        }, 500);
        return () => clearInterval(checkHandlersInterval);
      } else if (data.isScheduled && data.scheduleDate && data.scheduleTime) {
        const checkHandlersInterval = setInterval(() => {
          if (handlersRef.current) {
            clearInterval(checkHandlersInterval);
            scheduleCampaign(config, data.scheduleDate, data.scheduleTime);
          }
        }, 500);
        return () => clearInterval(checkHandlersInterval);
      }
    } catch (err) {
      console.error("Failed to restore bulk campaign state:", err);
    }
  }, [executeSend, scheduleCampaign]);

  // Prevent browser close
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isSending || isScheduled) {
        e.preventDefault();
        e.returnValue = "An active bulk campaign or scheduled message is running. Closing this tab will stop the process.";
        return e.returnValue;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isSending, isScheduled]);

  // Clean timers
  useEffect(() => {
    return () => {
      if (scheduleTimerRef.current) {
        clearTimeout(scheduleTimerRef.current);
      }
    };
  }, []);

  return (
    <BulkCampaignContext.Provider
      value={{
        contacts,
        isSending,
        isScheduled,
        sendProgress,
        error,
        scheduleDate,
        scheduleTime,
        campaignConfig,
        showSummary,
        summaryStats,
        setContacts,
        setShowSummary,
        registerHandlers,
        startCampaign,
        scheduleCampaign,
        cancelSchedule,
        stopCampaign,
        clearCampaign,
        retryFailedContacts,
        sendDesktopNotification,
      }}
    >
      {children}
    </BulkCampaignContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- The provider and its public hook intentionally share one context module.
export function useBulkCampaign() {
  const context = useContext(BulkCampaignContext);
  if (!context) {
    throw new Error("useBulkCampaign must be used within a BulkCampaignProvider");
  }
  return context;
}
